import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const PrivacyPolicyContent = () => (
  <div className="max-w-3xl mx-auto bg-white shadow-sm rounded-lg p-8 space-y-6">
    <header>
      <p className="text-sm uppercase tracking-wide text-indigo-500 font-semibold">Legal</p>
      <h1 className="text-3xl font-bold text-gray-900 mt-2">Privacy Policy</h1>
      <p className="text-gray-600 mt-2">
        Last updated: {new Date().toLocaleDateString()}
      </p>
      <p className="text-gray-600">
        The PromptCRM: Smart Email Management is operated by Animatic Media Inc. (“we”, “our”, or “the Service”).
        This Privacy Policy explains how we collect, use, and protect personal information when users access our platform.
      </p>
    </header>

    {/* Information Collection */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">1. Information We Collect</h2>

      <p className="text-gray-600">
        When users create or use an account, we may collect:
      </p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Name and email address</li>
        <li>Account login credentials</li>
        <li>Company or team information</li>
        <li>Campaign and contact data entered by users</li>
      </ul>

      <p className="text-gray-600">
        If users connect their Google account, we may access limited Google user data via Google APIs, including:
      </p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Permission to send emails through Gmail</li>
        <li>Email metadata such as sender, subject, timestamps, and message identifiers</li>
        <li>Google account email address</li>
      </ul>

      <p className="text-gray-600">
        We do NOT access or store full email message content unless required to send user-initiated email campaigns.
      </p>
    </section>

    {/* How We Use Data */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">2. How We Use Information</h2>

      <p className="text-gray-600">Collected information is used to:</p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Send email campaigns on behalf of users</li>
        <li>Track delivery and reply status</li>
        <li>Provide campaign analytics and reporting</li>
        <li>Support team collaboration features</li>
        <li>Maintain system security and reliability</li>
        <li>Provide customer support</li>
      </ul>

      <p className="text-gray-600">
        Google user data is only used to provide core functionality of the PromptCRM: Smart Email Management.
      </p>
    </section>

    {/* Storage & Security */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">3. Data Storage and Security</h2>

      <p className="text-gray-600">
        We implement industry-standard safeguards including:
      </p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Encrypted HTTPS connections</li>
        <li>Secure OAuth token storage</li>
        <li>Role-based access controls</li>
        <li>System monitoring and logging</li>
      </ul>
    </section>

    {/* Sharing */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">4. Data Sharing</h2>

      <p className="text-gray-600">
        We do not sell or rent personal data or Google user data.
      </p>

      <p className="text-gray-600">
        Data may be shared only when:
      </p>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Required by law</li>
        <li>Necessary to operate essential infrastructure such as cloud hosting</li>
      </ul>

      <p className="text-gray-600">
        We do not use Google user data for advertising purposes.
      </p>
    </section>

    {/* Retention */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">5. Data Retention</h2>

      <p className="text-gray-600">
        User data is retained only as long as necessary to provide service functionality.
      </p>

      <p className="text-gray-600">
        Users may request deletion of account data or revoke Google access at any time through Google account security settings.
      </p>
    </section>

    {/* User Rights */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">6. User Rights and Control</h2>

      <ul className="list-disc list-inside text-gray-600 space-y-2">
        <li>Update account information</li>
        <li>Disconnect Google integrations</li>
        <li>Request account or data deletion</li>
        <li>Export campaign data when available</li>
      </ul>
    </section>

    {/* Google Policy */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">7. Google API Services Compliance</h2>

      <p className="text-gray-600">
        Our use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy,
        including the Limited Use requirements.
      </p>
    </section>

    {/* Children */}
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">8. Children's Privacy</h2>

      <p className="text-gray-600">
        This service is not intended for individuals under the age of 13.
      </p>
    </section>

    {/* Contact */}
    <section className="space-y-2">
      <h2 className="text-xl font-semibold text-gray-800">9. Contact Information</h2>

      <p className="text-gray-600">
        For privacy questions or data requests, contact:
      </p>

      <p>
        <a
          href="mailto:support@animaticmedia.com"
          className="text-indigo-600 hover:text-indigo-500"
        >
          support@animaticmedia.com
        </a>
      </p>
    </section>
  </div>
);

const PrivacyPolicyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard?tab=privacy', { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <PrivacyPolicyContent />
    </div>
  );
};

export default PrivacyPolicyPage;
